/**
 * UI-6b Task 15 — Widget configuration dialog.
 *
 * Opens when an admin clicks the gear icon on a placed widget in the
 * composer. Renders:
 *   - A size selector (1x1 / 2x1 / 1x2 / 2x2) — fixed grid, drag-to-resize
 *     is Phase 2.
 *   - The widget-specific config editor, looked up from
 *     WIDGET_REGISTRY[instance.widgetType].ConfigEditor.
 *
 * The component holds a *local* copy of the instance while the dialog is
 * open; only Save propagates back via `onSave`. Cancel discards. This
 * matches the "edit-buffered" pattern from other admin drawers
 * (SipTrunkDrawer, ScheduledReportDrawer) so the parent doesn't see
 * intermediate states.
 */
import { useEffect, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { SelectButton } from 'primereact/selectbutton'
import { WIDGET_REGISTRY } from './widgets/index.js'
import type { WidgetInstance, WidgetSize } from './widgets/types.js'

export interface WidgetConfigDialogProps {
  open: boolean
  instance: WidgetInstance | null
  onClose: () => void
  onSave: (next: WidgetInstance) => void
}

const SIZE_OPTIONS: ReadonlyArray<{ label: string; value: WidgetSize }> = [
  { label: '1x1', value: '1x1' },
  { label: '2x1', value: '2x1' },
  { label: '1x2', value: '1x2' },
  { label: '2x2', value: '2x2' },
]

export default function WidgetConfigDialog(props: WidgetConfigDialogProps): JSX.Element | null {
  const { open, instance, onClose, onSave } = props
  const [draft, setDraft] = useState<WidgetInstance | null>(instance)

  // Reset the local buffer whenever the dialog opens against a new
  // instance — Cancel must restore the original, Save must persist the draft.
  useEffect(() => {
    setDraft(instance)
  }, [instance])

  if (!open || !instance || !draft) return null

  const manifest = WIDGET_REGISTRY[draft.widgetType]
  const ConfigEditor = manifest?.ConfigEditor

  function handleSave(): void {
    if (draft) onSave(draft)
  }

  function handleSizeChange(next: WidgetSize): void {
    setDraft((prev) => (prev ? { ...prev, size: next } : prev))
  }

  function handleConfigChange(nextConfig: unknown): void {
    setDraft((prev) =>
      prev ? { ...prev, config: nextConfig as Record<string, unknown> } : prev,
    )
  }

  return (
    <Dialog
      visible={open}
      onHide={onClose}
      header={manifest ? `Configure ${manifest.label}` : 'Configure widget'}
      style={{ width: '32rem' }}
      modal
    >
      <div className="space-y-4 text-sm" data-testid="widget-config-dialog">
        <div className="flex flex-col gap-2">
          <span className="font-medium" id="widget-size-label">
            Size
          </span>
          <SelectButton
            value={draft.size}
            options={[...SIZE_OPTIONS]}
            onChange={(e: { value: WidgetSize | null }): void => {
              // PrimeReact emits null when re-clicking the active option; ignore.
              if (e.value) handleSizeChange(e.value)
            }}
            aria-labelledby="widget-size-label"
            aria-label="Widget size"
          />
        </div>

        <div className="flex flex-col gap-2">
          <span className="font-medium">Settings</span>
          {ConfigEditor ? (
            <ConfigEditor
              config={draft.config}
              onChange={handleConfigChange}
            />
          ) : (
            <div className="text-xs text-gray-500" data-testid="widget-config-unknown">
              Unknown widget type — no settings available.
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button label="Cancel" onClick={onClose} severity="secondary" />
          <Button label="Save" onClick={handleSave} />
        </div>
      </div>
    </Dialog>
  )
}
